import React, { useState } from 'react';
import api from '../api';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PrimaryButton from '../components/PrimaryButton';
import Alert from '@mui/material/Alert';
import { isApplicationClosed } from '../utils/applicationGate';

function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [serverError, setServerError] = useState('');
  const [otpRequired, setOtpRequired] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBlur = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const toggleShowPassword = () => setShowPassword((s) => !s);

  const validation = {
    email: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email),
    password: form.password.trim().length > 0,
  };

  const formValid = Object.values(validation).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!formValid) return;
    try {
      setSubmitting(true);
      setServerError('');
      setOtpRequired(false);
      setFieldErrors({});
      const res = await api.post('/api/auth/login', null, { params: form });
      const data = res?.data;
      if (data && data.success && data.token) {
        localStorage.setItem('token', data.token);
        try {
          const winners = await api.get('/api/winners-assets/context');
          if (winners?.data?.eligible || isApplicationClosed()) {
            navigate('/winners-assets');
            return;
          }
        } catch (err) {
          setServerError('Login succeeded, but the winners portal could not be checked. Please try again.');
          return;
        }
        // determine next step based on whether school info/profile is completed
        try {
          const me = await api.get('/api/public/me');
          if (me?.data?.authenticated) {
            // If this is a first-login onboarding flow, route to InviteCollab for ADMINs
            const showInvite = (() => {
              try { return localStorage.getItem('showInviteOnFirstLogin') === 'true'; } catch (e) { return false; }
            })();
            if (showInvite && me?.data?.role === 'ADMIN') {
              try { localStorage.removeItem('showInviteOnFirstLogin'); } catch (e) { }
              navigate('/invitecollab', { state: { showInviteOnFirstLogin: true } });
              return;
            }

            const pr = await api.get('/api/public/school-profile');
            if (pr?.data?.exists && pr.data.completed) {
              localStorage.setItem('schoolProfileCompleted', 'true');
              navigate('/grantlanding');
            } else {
              localStorage.setItem('schoolProfileCompleted', 'false');
              navigate('/schoolinfo');
            }
            return;
          }
        } catch (err) {
          // fallback
          navigate('/schoolinfo');
          return;
        }
      }
      if (data && data.fieldErrors) {
        setFieldErrors(data.fieldErrors);
        return;
      }
      setServerError(data?.message || 'Invalid credentials');
    } catch (err) {
      const data = err?.response?.data;
      // Handle OTP required (password correct but OTP not completed)
      if (err?.response?.status === 403 && data?.code === 'OTP_REQUIRED') {
        setOtpRequired(true);
        setServerError(data?.message || 'Account not verified. Enter the OTP sent to your email or request a new code.');
      } else if (data?.fieldErrors) setFieldErrors(data.fieldErrors);
      else if (data?.message) setServerError(data.message);
      else setServerError('Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="main">
      <Grid container justifyContent="center" sx={{ mt: 4 }}>
        <Grid item xs={12} md={6} lg={5}>
          <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 3, py: { xs: 1.5, md: 2 } }}>
              <Box
                component="img"
                src="/ryslogo.png"
                alt="Retool Your School"
                sx={{ height: { xs: 42, md: 60 }, objectFit: 'contain' }}
              />
            </Box>

            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
                Grant Application Login
              </Typography>

              {serverError ? <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert> : null}
              {otpRequired && (
                <Alert
                  severity="info"
                  sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  action={
                    <PrimaryButton
                      size="small"
                      onClick={async () => {
                        if (!form.email) {
                          setServerError('Please enter your email above to request a new code.');
                          return;
                        }
                        try {
                          setSubmitting(true);
                          setServerError('');
                          await api.post('/api/auth/resend-otp', null, { params: { email: form.email } });
                          try { localStorage.setItem('pendingEmail', form.email); } catch (e) { }
                          navigate(`/otp?email=${encodeURIComponent(form.email)}`);
                        } catch (err) {
                          setServerError(err?.response?.data || 'Failed to resend code');
                        } finally { setSubmitting(false); }
                      }}
                    >
                      Resend Code
                    </PrimaryButton>
                  }
                >
                  {serverError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="email"
                      type="email"
                      label="Email"
                      placeholder="Enter your email"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur('email')}
                      error={Boolean(fieldErrors.email) || (touched.email && !validation.email)}
                      helperText={fieldErrors.email || (touched.email && !validation.email ? 'Enter a valid email' : '')}
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      label="Password"
                      placeholder="Enter your password"
                      value={form.password}
                      onChange={handleChange}
                      onBlur={() => handleBlur('password')}
                      error={Boolean(fieldErrors.password) || (touched.password && !validation.password)}
                      helperText={fieldErrors.password || (touched.password && !validation.password ? 'Password is required' : '')}
                      fullWidth
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              onClick={toggleShowPassword}
                              edge="end"
                              size="large"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <PrimaryButton type="submit" disabled={!formValid || submitting}>
                        {submitting ? 'Signing in...' : 'Login'}
                      </PrimaryButton>
                      <Box>
                        <Link to="/forgotpassword">Forgot Password?</Link>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ mt: 1 }}>Don't have an account yet?<br></br> Check your eligibility <Link to="/">HERE</Link>.</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Login;
